import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Send, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  not_connected: { label: "Email Sending Not Connected", cls: "bg-slate-100 text-slate-600", icon: XCircle },
  sender_not_verified: { label: "Sender Not Verified", cls: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  ready: { label: "Ready to Send", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

const LOG_CLS = { sent: "bg-emerald-100 text-emerald-700", queued: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700" };

export default function EmailSettings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ resend_api_key: "", sender_name: "", sender_email: "", verified: false });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/email").then(({ data }) => {
    setCfg(data);
    setF({ resend_api_key: "", sender_name: data.sender_name || "", sender_email: data.sender_email || "", verified: data.verified });
  }).catch(() => setCfg(false));
  const loadLogs = () => api.get("/settings/email/log").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  useEffect(() => { load(); loadLogs(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { sender_name: f.sender_name, sender_email: f.sender_email, verified: f.verified };
      if (f.resend_api_key.trim()) body.resend_api_key = f.resend_api_key.trim();
      await api.put("/settings/email", body);
      toast.success("Email settings saved");
      load(); loadLogs();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = detail ? formatApiErrorDetail(detail) : (e.response ? `Save failed (HTTP ${e.response.status})` : e.message || "Save failed");
      toast.error(msg);
    }
    setBusy(false);
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a recipient email");
    setBusy(true);
    try {
      const { data } = await api.post("/settings/email/test", { recipient: testTo });
      if (data.status === "sent") toast.success("Test email sent!");
      else if (data.status === "queued") toast.message("Queued — no API key configured yet.");
      else toast.error(`Failed: ${data.error || data.reason || "unknown"}`);
      loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const flush = async () => {
    setBusy(true);
    try { const { data } = await api.post("/settings/email/flush"); toast.success(`Processed ${data.processed || 0}: ${data.sent || 0} sent, ${data.failed || 0} failed`); load(); loadLogs(); }
    catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  if (cfg === false) return <DashboardLayout nav={getNav(user.role)} title="Email Settings"><p>Unable to load.</p></DashboardLayout>;

  const meta = cfg ? (STATUS_META[cfg.status] || STATUS_META.not_connected) : STATUS_META.not_connected;
  const StatusIcon = meta.icon;

  return (
    <DashboardLayout nav={getNav(user.role)} title="Email Settings (Resend)">
      {!cfg ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="max-w-3xl space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" /><h2 className="font-head font-semibold text-slate-900">Delivery Status</h2></div>
              <span data-testid="email-status-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${meta.cls}`}><StatusIcon className="w-3.5 h-3.5" />{meta.label}</span>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs uppercase font-bold text-slate-400">API Key</div><div className="mt-1 font-mono text-slate-800" data-testid="email-masked-key">{cfg.masked_key || "Not set"}</div></div>
              <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs uppercase font-bold text-slate-400">Sender Verified</div><div className="mt-1 text-slate-800">{cfg.verified ? "Yes" : "No"}</div></div>
              <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs uppercase font-bold text-slate-400">Queued Emails</div><div className="mt-1 text-slate-800 font-semibold" data-testid="email-queued-count">{cfg.queued_count}</div></div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="font-head font-semibold text-slate-900">Resend Configuration</h2>
            <div>
              <Label>Resend API Key</Label>
              <PasswordInput data-testid="email-key-input" value={f.resend_api_key} onChange={(e) => setF({ ...f, resend_api_key: e.target.value })} className="mt-1.5" placeholder={cfg.connected ? `Saved (${cfg.masked_key}). Enter a new key to replace.` : "re_..."} />
              <p className="mt-1 text-xs text-slate-400">Stored securely on the backend. The full key is never displayed after saving.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Sender Name</Label><Input data-testid="email-sender-name" value={f.sender_name} onChange={(e) => setF({ ...f, sender_name: e.target.value })} className="mt-1.5" placeholder="SJ Property Care" /></div>
              <div><Label>Sender Email</Label><Input data-testid="email-sender-email" value={f.sender_email} onChange={(e) => setF({ ...f, sender_email: e.target.value })} className="mt-1.5" placeholder="notifications@yourdomain.com" /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><Label className="mb-0">Sender / Domain Verified</Label><p className="text-xs text-slate-400 mt-0.5">Confirm your sending domain is verified in Resend.</p></div>
              <Switch data-testid="email-verified-switch" checked={f.verified} onCheckedChange={(v) => setF({ ...f, verified: v })} />
            </div>
            <Button data-testid="email-save-btn" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Settings"}</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="font-head font-semibold text-slate-900">Test & Queue</h2>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]"><Label>Test Email Recipient</Label><Input data-testid="email-test-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="mt-1.5" placeholder="you@example.com" /></div>
              <Button data-testid="email-test-btn" onClick={sendTest} disabled={busy} variant="outline"><Send className="w-4 h-4 mr-1" /> Send Test Email</Button>
              <Button data-testid="email-flush-btn" onClick={flush} disabled={busy} variant="outline"><RefreshCw className="w-4 h-4 mr-1" /> Process Queue ({cfg.queued_count})</Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-head font-semibold text-slate-900">Delivery Log</h2><Button size="sm" variant="ghost" onClick={loadLogs}><RefreshCw className="w-3.5 h-3.5" /></Button></div>
            {logs.length === 0 ? <p className="text-sm text-slate-400">No email activity yet.</p> : (
              <div className="space-y-1.5" data-testid="email-log-list">
                {logs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-50 py-2">
                    <div className="min-w-0">
                      <div className="text-slate-800 truncate">{l.to} <span className="text-slate-400">· {l.type || "—"}</span></div>
                      <div className="text-xs text-slate-400 truncate">{l.subject}{l.error ? ` · ${l.error}` : ""} · {new Date(l.at).toLocaleString()}</div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${LOG_CLS[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
